import { useState, useEffect } from 'react';
import { auditAPI, integrationsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Settings,
  Shield,
  Database,
  FileSpreadsheet,
  Activity,
  Clock,
  User,
  Loader2,
  RefreshCw,
  ExternalLink,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

export default function SettingsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [sheetsConfigs, setSheetsConfigs] = useState([]);
  const [loadingConfigs, setLoadingConfigs] = useState(false);

  useEffect(() => {
    if (activeTab === 'audit' && user?.role === 'admin') {
      fetchAuditLogs();
    }
    if (activeTab === 'integrations' && user?.role === 'admin') {
      fetchSheetsConfigs();
    }
  }, [activeTab, user?.role]);

  const fetchAuditLogs = async () => {
    setLoadingLogs(true);
    try {
      const response = await auditAPI.getLogs({ limit: 50 });
      setAuditLogs(response.data);
    } catch (err) {
      toast.error('Failed to fetch audit logs');
    }
    setLoadingLogs(false);
  };

  const fetchSheetsConfigs = async () => {
    setLoadingConfigs(true);
    try {
      const response = await integrationsAPI.getGoogleSheetsConfigs();
      setSheetsConfigs(response.data);
    } catch (err) {
      console.log('No Google Sheets configs found');
    }
    setLoadingConfigs(false);
  };

  const handleSyncGoogleSheets = async (configId) => {
    try {
      await integrationsAPI.syncGoogleSheets(configId);
      toast.success('Sync triggered successfully');
      fetchSheetsConfigs();
    } catch (err) {
      toast.error('Failed to sync');
    }
  };

  const getActionColor = (action) => {
    if (action.includes('create')) return 'bg-green-100 text-green-800';
    if (action.includes('update')) return 'bg-blue-100 text-blue-800';
    if (action.includes('delete')) return 'bg-red-100 text-red-800';
    if (action.includes('login')) return 'bg-purple-100 text-purple-800';
    return 'bg-slate-100 text-slate-800';
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-1">
          Manage your account and system settings
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="general" data-testid="tab-general">General</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations">Integrations</TabsTrigger>
          {(user?.role === 'admin' || user?.role === 'physician') && (
            <TabsTrigger value="audit" data-testid="tab-audit">Audit Logs</TabsTrigger>
          )}
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Profile
              </CardTitle>
              <CardDescription>Your account information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input value={user?.full_name || ''} disabled data-testid="profile-name" />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={user?.email || ''} disabled data-testid="profile-email" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <div>
                  <Badge className="capitalize">{user?.role}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Security
              </CardTitle>
              <CardDescription>Security settings and session info</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Session Timeout</p>
                    <p className="text-sm text-muted-foreground">
                      Sessions automatically expire after 8 hours of inactivity
                    </p>
                  </div>
                </div>
                <Badge variant="secondary">8 hours</Badge>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <Database className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Data Encryption</p>
                    <p className="text-sm text-muted-foreground">
                      All data is encrypted at rest and in transit
                    </p>
                  </div>
                </div>
                <Badge className="bg-green-100 text-green-800">Enabled</Badge>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations */}
        <TabsContent value="integrations" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5" />
                Google Sheets Integration
              </CardTitle>
              <CardDescription>
                Sync form submissions to Google Sheets (Mock - Configure for production)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {user?.role !== 'admin' ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Shield className="h-12 w-12 mx-auto mb-4 opacity-20" />
                  <p>Admin access required to manage integrations</p>
                </div>
              ) : loadingConfigs ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : sheetsConfigs.length === 0 ? (
                <div className="text-center py-8">
                  <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-20" />
                  <h3 className="text-lg font-medium mb-2">No Configurations</h3>
                  <p className="text-muted-foreground mb-4">
                    Google Sheets integration is available for configuration.
                    Contact support to set up real-time sync.
                  </p>
                  <Badge variant="outline">MOCK - Ready for Integration</Badge>
                </div>
              ) : (
                <div className="space-y-4">
                  {sheetsConfigs.map(config => (
                    <div key={config.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium">Sheet: {config.sheet_name}</p>
                        <p className="text-sm text-muted-foreground">
                          Last sync: {config.last_sync ? formatDateTime(config.last_sync) : 'Never'}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSyncGoogleSheets(config.id)}
                        className="gap-2"
                      >
                        <RefreshCw className="h-4 w-4" />
                        Sync Now
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Available Integrations</CardTitle>
              <CardDescription>Future integration options</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                {[
                  { name: 'Billing Module', status: 'Coming Soon', icon: '💳' },
                  { name: 'Patient Portal', status: 'Coming Soon', icon: '🏥' },
                  { name: 'Analytics Dashboard', status: 'Coming Soon', icon: '📊' },
                  { name: 'HL7/FHIR Integration', status: 'Coming Soon', icon: '🔗' },
                ].map(integration => (
                  <div key={integration.name} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{integration.icon}</span>
                      <span className="font-medium">{integration.name}</span>
                    </div>
                    <Badge variant="outline">{integration.status}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Logs */}
        <TabsContent value="audit">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Audit Logs
                </CardTitle>
                <CardDescription>System activity and user actions</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchAuditLogs} className="gap-2">
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {loadingLogs ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : auditLogs.length === 0 ? (
                <div className="text-center py-12">
                  <Activity className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-20" />
                  <p className="text-muted-foreground">No audit logs found</p>
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Action</TableHead>
                        <TableHead>Entity</TableHead>
                        <TableHead className="hidden md:table-cell">User</TableHead>
                        <TableHead>Timestamp</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {auditLogs.map((log) => (
                        <TableRow key={log.id} data-testid={`audit-log-${log.id}`}>
                          <TableCell>
                            <Badge className={getActionColor(log.action)}>
                              {log.action.replace(/_/g, ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <code className="text-xs bg-muted px-2 py-1 rounded">
                              {log.entity_type}
                            </code>
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            {log.user_email}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDateTime(log.timestamp)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
